import { useState, type ElementType } from 'react';
import { Users, FolderOpen, BarChart2, Activity, ArrowLeft, ShieldCheck, Zap, Plug } from 'lucide-react';
import AdminUsers from './AdminUsers';
import AdminProjects from './AdminProjects';
import AdminAnalytics from './AdminAnalytics';
import AdminParserHealth from './AdminParserHealth';
import AdminUtilityDemand from './AdminUtilityDemand';
import AdminUtilities from './AdminUtilities';

type AdminTab = 'users' | 'projects' | 'analytics' | 'parser-health' | 'utility-demand' | 'utilities';

interface AdminDashboardProps {
  onClose: () => void;
}

const NAV_ITEMS: { id: AdminTab; label: string; Icon: ElementType }[] = [
  { id: 'users', label: 'Users', Icon: Users },
  { id: 'projects', label: 'Projects', Icon: FolderOpen },
  { id: 'analytics', label: 'Analytics', Icon: BarChart2 },
  { id: 'parser-health', label: 'Parser Health', Icon: Activity },
  { id: 'utility-demand', label: 'Utility Demand', Icon: Zap },
  { id: 'utilities', label: 'Utilities', Icon: Plug },
];

export default function AdminDashboard({ onClose }: AdminDashboardProps) {
  const [activeTab, setActiveTab] = useState<AdminTab>('users');

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-4">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to App</span>
          </button>
          <div className="h-5 border-l border-gray-300" />
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-purple-600" />
            <h1 className="text-lg font-bold text-gray-900">Admin Dashboard</h1>
          </div>
        </div>
      </header>

      <div className="flex-1 flex flex-col md:flex-row max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6 gap-6">
        {/* Left nav — desktop only */}
        <nav className="hidden md:block w-48 flex-shrink-0">
          <ul className="space-y-1">
            {NAV_ITEMS.map(({ id, label, Icon }) => (
              <li key={id}>
                <button
                  data-testid={
                    id === 'parser-health'
                      ? 'parser-health-nav'
                      : id === 'utility-demand'
                        ? 'utility-demand-nav'
                        : id === 'utilities'
                          ? 'utilities-nav'
                          : undefined
                  }
                  onClick={() => setActiveTab(id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === id
                      ? 'bg-purple-100 text-purple-800'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-400 px-3 leading-relaxed">
              Admin actions are logged. All views are read-only except user status and role, and the Utilities reference data.
            </p>
          </div>
        </nav>

        {/* Content */}
        <main className="flex-1 min-w-0">
          {/* Mobile tab bar */}
          <div className="md:hidden mb-4">
            <div className="flex">
              {NAV_ITEMS.map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`flex-1 py-2.5 text-sm font-medium text-center transition-colors border-b-2 ${
                    activeTab === id
                      ? 'text-purple-700 border-purple-600'
                      : 'text-gray-500 border-gray-200 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {activeTab === 'users' && <AdminUsers />}
          {activeTab === 'projects' && <AdminProjects />}
          {activeTab === 'analytics' && <AdminAnalytics />}
          {activeTab === 'parser-health' && <AdminParserHealth />}
          {activeTab === 'utility-demand' && <AdminUtilityDemand />}
          {activeTab === 'utilities' && <AdminUtilities />}
        </main>
      </div>
    </div>
  );
}
